/**
 * Client-side presentation for every `navigationConfigKey`.
 *
 * A NAVIGATION artifact only qualifies the logged-in user for a config — it does not describe the
 * item. Everything a supplier sees is written here and assembled in `NavigationContext`, the same
 * way lending and FinanceIQ are. For a config-driven artifact, `content.text`, `content.icon`,
 * `content.target`, and `content.children` are ignored.
 *
 * CMT keeps its own copy for the authoring preview and its locked fields, in
 * `apps/admin/ui/src/app/pages/CampaignManagement/assets/components/navigationConfigPresets.ts`.
 * The two are duplicated on purpose, the same way `MarketingWidgetTemplate` is duplicated as
 * `SideCardPreviewTemplate`: a label, icon, route, or child change here has to be copied there.
 * Enterprise is the final truth when they disagree.
 *
 * Labels are hardcoded English. No NAVIGATION asset has ever carried a translation, and CMT dropped
 * NAVIGATION from its translation system in SUPP-19609, so there is nothing to look up yet.
 */
import { BoxCheckDuotoneIcon, ChartNetworkDuotoneIcon } from '@c2fo/liquidity/icons';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import type { NavigationItem } from './core/Navigation';

/**
 * Fundbox is a single row. `id` is local UI state, the same kind of value as `id: 'lending'` — it is
 * deliberately not the `navigationConfigKey`.
 */
export const FUNDBOX_NAVIGATION_CONFIG: NavigationItem = {
  id: 'fundbox',
  label: 'Term Loans by Fundbox',
  Icon: BoxCheckDuotoneIcon,
  path: asEnterpriseLink('termloansbyfundbox'),
};

/**
 * Extended Network Finance renders one of two shapes, chosen by the `useEnfVersion` treatment and
 * never by the payload: `0.0` is `leaf`, `0.1` is `nested`, `off` renders nothing.
 */
export const EXTENDED_NETWORK_FINANCE_NAVIGATION_CONFIG: {
  leaf: NavigationItem;
  nested: NavigationItem;
} = {
  leaf: {
    id: 'extended-network-finance',
    label: 'Extended Network Finance',
    Icon: ChartNetworkDuotoneIcon,
    path: asEnterpriseLink('extended-network-finance'),
  },
  nested: {
    id: 'extended-network-finance',
    label: 'Extended Network Finance',
    Icon: ChartNetworkDuotoneIcon,
    subItems: [
      {
        id: 'extended-network-finance-learn-more',
        label: 'Learn More',
        path: asEnterpriseLink('extended-network-finance'),
      },
      {
        id: 'extended-network-finance-get-a-quote',
        label: 'Get a Quote',
        path: asEnterpriseLink('extended-network-finance/get-a-quote'),
      },
    ],
  },
};
